// The `srvErr.<code>` lookup — brief 38 §3, and the connection of a mechanism
// that has existed twice and was never wired either time.
//
// Phase M+3.7 seeded 29 `srvErr.*` rows in three languages for a computed
// lookup that was never written; `server/src/lib/serverI18n.js` was then built
// as its replacement and never imported. `git log -S` on both the template-
// literal and the concatenation form finds no `srvErr.` lookup in the history
// of `src/`. So this file is not a third mechanism — it is the first one,
// connected. Nothing here invents a key, a word or a convention: the key is
// `srvErr.` + the code the server already sends, and every row it reads was
// seeded years ago.
//
// WHERE IT RUNS. At the ENVELOPE, not at the toast. `src/api.ts` is the single
// place where a `{ error, code }` response becomes a client-visible string
// (`err.message`), so resolving here reaches every consumer downstream —
// `_apiErr`'s else-arm, every `catch (e) => toast(e.message)`, the compact
// column, the modals. Brief 38 §1 recorded that `srvErr.m365.not_connected`
// handling "exists on SOME client paths, which is not the same as all of
// them"; one seam is what makes it all of them. `src/lib/invoiceFetch.ts`
// bypasses `api()` with its own `fetch`, so it calls this too.
//
// WHY THE ENGLISH ROW DECIDES, AND NOT THE CODE ALONE. A blind
// `t("srvErr." + code)` would be right for 19 codes and wrong for three, and
// the three are the biggest: `generic.bad_request` carries 106 distinct
// messages, `generic.not_found` 12, `generic.forbidden` 10 (measured against
// server/src). Substituting the code's row for a specific sentence turns "You
// already have three responsible partners" into "Requête invalide" — which is
// exactly what brief 38 §6 forbids ("keying by code would collapse a hundred
// specific, useful sentences into one"), and it would destroy the material
// §5's investigation of those hundred needs.
//
// So the row's ENGLISH is the key's identity. `srvErr.<code>` is used when the
// row's `en` IS the message the server sent — then the French row is a
// translation OF THAT MESSAGE, and swapping it in changes the language and
// nothing else. Where they differ, the server's sentence is the more specific
// of the two and is kept until §6 gives that message its own key. This is
// per-MESSAGE keying, done with the data already in the seed, and it needs no
// list of codes for anyone to keep up to date.
//
// §6 IS THE REST OF THAT SENTENCE, and it is one key SHAPE, not one mechanism
// more (brief 38 unit 4b). A code carries many messages — `generic.not_found`
// 12, `generic.required_field` 39 — so ONE row per code can only ever be the
// identity of ONE of them. The key therefore has a family:
//
//     srvErr.<code>            the CODE's own label — what a client is told
//                              when the message slot is empty or holds an
//                              identifier. Unchanged, and still 51 seeded rows.
//     srvErr.<code>.<leaf>     ONE MESSAGE under that code, `leaf` a slug of
//                              its English. 227 of them, written by unit 4b.
//
// The rule is identical either way and is the ruled one: THE ROW'S `en` IS THE
// MESSAGE THE SERVER SENT. Nothing is keyed by code, nothing is keyed by text
// alone — a message is (code, English), which is exactly §6's "not per code,
// not per text", and the 10 texts that sit under more than one code get one row
// per code because they are different concepts (§0(ii), no dedup).
//
// The client never has to know the leaf: it looks the message up BY ITS
// ENGLISH, inside the family the code names. So the server sends what it
// always sent, the response contract §5 calls forever is untouched, and no
// route was rewritten to make this work.
//
// A FIELD NAME IS PART OF THE SENTENCE, NOT A SLOT (unit 4b's ruling). The seed
// holds four parameterised rows — `{field} is required` and its siblings — and
// they stay INERT: nothing interpolates them and 4b did not make anything do
// so. `{field} est requis` cannot agree in French, and the seed proves it
// twice over in rows written before this one: `name required` is `nom requis`
// and `vars required` is `variables requiseS`. One template renders "variables
// est requis" — wrong in gender and in number, and no lint can find it,
// because the deciding fact (the gender of the noun) is not in the data. So
// the field is named IN each language, inside the message's own row, where a
// human decided once whether the token is a word the user sees (`caseId` →
// `dossier`) or a contract token they must match (`billitOrderId`, which the
// seed already keeps verbatim). The structured `field:` that 4a put on 116
// sites is untouched and is not the words — it is for a client that wants to
// point at the offending input.
//
// AN IDENTIFIER IS NOT A WORD (RULINGS.md, 2026-08-04, Laurent). A second,
// narrower arm: when the message slot carries a machine identifier rather than
// a sentence, a dictionary row for its code is unambiguously better than what
// the user is being shown. This fires on `m365_not_connected`, the internal
// sentinel `server/src/lib/m365.js` returns from 19 sites, which reaches a
// user verbatim on five paths — see the PR body. It closes one of those five;
// the other four carry codes with no seeded row and belong to the unit that
// rules on the envelope contract.
//
// LIMIT, STATED. The identity test compares text, so an edit to a server
// message that does not also edit the seed row silently stops that message
// resolving — it degrades to today's behaviour (the English the server sent),
// never to something worse. Nothing is hidden by it either: that message stays
// counted among gate 1's 739 in `scripts/lint/i18n-total.js` until §6 keys it.

const KEY_PREFIX = "srvErr.";

type Catalog = Record<string, Record<string, string>>;

/** The catalogue + active language `I18nProvider` publishes for call sites
 *  outside the React tree (`src/i18n.tsx:109`, whose own comment says it is
 *  there "so server-translated message helpers … can read the live value" —
 *  the third piece of the mechanism that was placed and never used). */
function catalogue(): { data: Catalog; lang: string } | null {
  if (typeof window === "undefined" || !window.__lxI18n) return null;
  const { data: published, lang } = window.__lxI18n;
  // 208 §20 — a window global, checked whatever its declared type (the bridge now carries the catalog's)
  const data: unknown = published;
  if (!data || typeof data !== "object") return null;
  return { data: data as Catalog, lang: lang || "en" };
}

/** Trim, collapse runs of whitespace, fold case. Nothing else — case and
 *  spacing never separate two concepts, and stripping more would risk it.
 *  Without the fold, `generic.invalid_id` loses all 15 of its sites to the
 *  capital in the seed's "Invalid id" against the server's "invalid id". */
const norm = (s: string) => s.trim().replace(/[ \t\r\n]+/g, " ").toLowerCase();

/** A message slot holding a machine identifier: one token, no spaces, and
 *  either snake_case or the code itself echoed back. `m365_not_connected`
 *  qualifies; `Forbidden` does not. */
function isIdentifier(text: string, code: string): boolean {
  const t = text.trim();
  if (!t || /[ \t\r\n]/.test(t)) return false;
  return t.includes("_") || t === code;
}

/** `norm(en)` → the keys carrying that English, over the `srvErr.` namespace
 *  only. Built once per catalogue: the alternative is walking 3,400 keys on
 *  every failed request, and a failed request is not a moment to be slow in.
 *  Rebuilt when the catalogue object changes, which is what `I18nProvider`
 *  hands over on a language switch (`src/i18n.tsx:113`). */
let indexedCatalog: Catalog | null = null;
let byEnglish: Map<string, string[]> = new Map();

function familyIndex(data: Catalog): Map<string, string[]> {
  if (indexedCatalog === data) return byEnglish;
  const next = new Map<string, string[]>();
  const en = data.en || {};
  for (const key of Object.keys(en)) {
    if (!key.startsWith(KEY_PREFIX)) continue;
    const k = norm(en[key]);
    const at = next.get(k);
    if (at) at.push(key);
    else next.set(k, [key]);
  }
  indexedCatalog = data;
  byEnglish = next;
  return next;
}

/** The key whose `en` IS this message, INSIDE this code's family — the code's
 *  own row or one of its `.<leaf>` rows. A text that sits under several codes
 *  has one row per code and the family is what tells them apart. */
function keyForMessage(data: Catalog, code: string, message: string): string | null {
  const hits = familyIndex(data).get(norm(message));
  if (!hits) return null;
  const self = KEY_PREFIX + code;
  const prefix = self + ".";
  for (const k of hits) if (k === self || k.startsWith(prefix)) return k;
  return null;
}

// ── A ROW WHOSE ENGLISH CARRIES {token} SLOTS IS A PATTERN ──────────────────
//
// Brief 38's last server-envelope unit. 227 messages were keyed by unit 4b
// because each one IS a string literal at its route; 41 (code, message) pairs
// were not, and 23 of those are TEMPLATES — `items[${i}].caseId must be a
// positive integer`. No row's `en` can BE a rendered template, because the
// rendering differs on every request, so the identity test above can never
// match one and unit 4b left them counted.
//
// THE SEED HAS HELD THE WORDS FOR THESE SINCE PHASE M+3.7. Ten `srvErr.*` rows
// carry a `{token}` in all three languages — `Unknown sectionKey: {sectionKey}`,
// `items[{i}].{field} required`, `Graph call failed: {message}` — written for
// the computed lookup this file's header describes as never wired. What was
// missing was never the translation. It was that nothing could tell that
// `items[3].subject required` is that row.
//
// SO THE IDENTITY IS THE FRAME, and the rule above is unchanged in shape: the
// row's ENGLISH is still what says which message this is. A patterned row's
// English is read as literal frame parts with holes between them; the message
// matches when its frame is that frame, and what sat in the holes is carried
// across into the active language's row. Nothing is looked up by code alone,
// nothing is looked up by text alone.
//
// AND THE HOLES ARE NEVER WORDS — which is unit 4b's ruling, obeyed rather than
// re-taken. `{field} est requis` cannot agree in French, so 4b refused to
// interpolate a noun and named the field inside each language's row instead.
// Every slot reaching this arm is an array index, a JSON property name, a row
// id, an HTTP status or a third party's own sentence: none of them declines,
// agrees, or needs an article. Where a slot WOULD be a word the route still
// sends the sentence whole and this arm never sees it — `Only admins can change
// ${label}` (server/src/routes/collaborators.js:169) is exactly that case and is
// deliberately left unkeyed and counted.
//
// EXACT BEFORE PATTERNED, ALWAYS. `members must be an array` has a row of its
// own (unit 4b wrote it from a literal at its route) AND matches the patterned
// `{field} must be an array`. The exact row wins because it is the more specific
// statement about that message, and because that ordering is what lets a
// template's row and its siblings' rows coexist in one family.
//
// AMBIGUITY REFUSES. If two patterned rows in the family match, this returns
// null and the server's English stays on screen — the same "leave it alone"
// answer as every other miss here. Guessing between two rows could put a value
// in the wrong hole in a language whose word order differs from English, which
// is the one way this arm could produce something WORSE than the English it
// replaces.
const SLOT = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
const collapse = (s: string) => s.trim().replace(/[ \t\r\n]+/g, " ");
const escapeRx = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The literal frame parts of a patterned row, or null if it is not one. */
function framesOf(en: string): { parts: string[]; names: string[] } | null {
  const parts: string[] = [];
  const names: string[] = [];
  let last = 0;
  SLOT.lastIndex = 0;
  for (let m = SLOT.exec(en); m; m = SLOT.exec(en)) {
    parts.push(en.slice(last, m.index));
    names.push(m[1]);
    last = m.index + m[0].length;
  }
  if (!names.length) return null;
  parts.push(en.slice(last));
  // Two adjacent slots — `{a}{b}` — make the split between them arbitrary.
  // Refusing is the only honest answer; no shipped row has this shape.
  for (let i = 1; i < parts.length - 1; i++) if (!parts[i]) return null;
  return { parts, names };
}

/** What sat in this row's holes, or null if the message is not this row. */
function slotsFor(en: string, message: string): Record<string, string> | null {
  const f = framesOf(collapse(en));
  if (!f) return null;
  const rx = new RegExp(
    "^" + f.parts.map(escapeRx).join("([\\s\\S]*?)") + "$",
    "i",
  );
  const hit = rx.exec(collapse(message));
  if (!hit) return null;
  const vars: Record<string, string> = {};
  f.names.forEach((n, i) => {
    vars[n] = hit[i + 1];
  });
  return vars;
}

/** Fill a row's slots in ONE pass, so a captured value that happens to contain
 *  `{x}` is never read back as a slot. Null when the row still has a hole this
 *  message cannot fill — a half-rendered sentence is worse than the English. */
function fill(row: string, vars: Record<string, string>): string | null {
  let missing = false;
  const out = row.replace(SLOT, (m, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name)
      ? vars[name]
      : ((missing = true), m),
  );
  return missing ? null : out;
}

/** The one patterned row in this code's family that this message is, with what
 *  sat in its holes. Null when none matches or more than one does. */
function patternForMessage(
  data: Catalog,
  code: string,
  message: string,
): { key: string; vars: Record<string, string> } | null {
  const en = data.en || {};
  const self = KEY_PREFIX + code;
  const prefix = self + ".";
  let hit: { key: string; vars: Record<string, string> } | null = null;
  for (const key of Object.keys(en)) {
    if (key !== self && !key.startsWith(prefix)) continue;
    const vars = slotsFor(en[key], message);
    if (!vars) continue;
    if (hit) return null;
    hit = { key, vars };
  }
  return hit;
}

/**
 * The user-language text for a server error envelope, or `null` when this
 * mechanism has nothing better to offer than what the server sent.
 *
 * `null` is the "leave it alone" answer and every caller treats it that way —
 * the server's English stays on screen, exactly as it does today.
 */
export function srvErrText(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const { error, code } = body as { error?: unknown; code?: unknown };
  if (typeof code !== "string" || !code) return null;

  const cat = catalogue();
  if (!cat) return null;

  const inLang = (k: string) =>
    (cat.data[cat.lang] && cat.data[cat.lang][k]) ||
    (cat.data.en && cat.data.en[k]) ||
    null;

  // THE MESSAGE'S OWN ROW, first, because it is the most specific thing there
  // is: a key in this code's family whose `en` IS this sentence. §6.
  if (typeof error === "string" && error.trim()) {
    const own = keyForMessage(cat.data, code, error);
    if (own) {
      const text = inLang(own);
      if (typeof text === "string" && text) return text;
    }
    // Then the message ASSEMBLED at its route: a row in this family whose
    // English is this sentence with holes in it. Second, never first — a row
    // that IS the message beats a row that merely fits it.
    const pat = patternForMessage(cat.data, code, error);
    if (pat) {
      const text = inLang(pat.key);
      if (typeof text === "string" && text) {
        const filled = fill(text, pat.vars);
        if (filled) return filled;
      }
    }
  }

  // Units 1–2's arm — "the CODE's row IS this message" — is not gone, it is
  // the `k === self` case above: `srvErr.<code>` is a member of its own family.
  // The 35 messages that resolved before this change resolve through it still.
  const key = KEY_PREFIX + code;
  const en = cat.data.en && cat.data.en[key];
  const active = (cat.data[cat.lang] && cat.data[cat.lang][key]) || en;
  if (typeof active !== "string" || !active) return null;
  // A CODE ROW WITH A HOLE IN IT IS NOT A LABEL. `srvErr.generic.required_field`
  // is `{field} is required`; returning it here would print the brace to a user.
  // The four parameterised rows unit 4b ruled INERT are exactly this shape, and
  // this is the arm that would have rendered them.
  if (framesOf(active)) return null;

  // No message at all: the code is all we have, and a row for it is words.
  if (typeof error !== "string" || !error.trim()) return active;

  // The message is an identifier. Words beat an identifier in any language.
  if (isIdentifier(error, code)) return active;

  // The server said something more specific than this code's label. Keep it;
  // §6 gives it a key of its own.
  return null;
}
